/**
 * Activity / audit timeline. Renders the conversation_activity_log entries
 * (newest first from the API) with a humanised description + actor + time.
 */
import { type JSX } from 'react'
import {
  ArrowLeftRight,
  CheckCircle2,
  CircleDot,
  Clock,
  Flag,
  Layers,
  Lock,
  MessageSquare,
  ShieldCheck,
  UserPlus,
  Activity as ActivityIcon,
} from 'lucide-react'
import { cn } from '../../../utils/cn.js'
import type { ActivityLogEntry } from '../../../modules/conversations/types.js'
import { formatInTimezone, formatTime, timezoneDayKey } from '../../../utils/timezone.js'

type IconConfig = { icon: typeof CircleDot; bg: string; text: string; ring: string; label: string }

function iconConfigFor(eventType: string): IconConfig {
  if (eventType.includes('transfer'))
    return { icon: ArrowLeftRight, bg: 'bg-violet-50', text: 'text-violet-600', ring: 'ring-violet-100/60', label: 'Transfer' }
  if (eventType.includes('assign'))
    return { icon: UserPlus, bg: 'bg-blue-50', text: 'text-blue-600', ring: 'ring-blue-100/60', label: 'Assignment' }
  if (eventType.includes('close') || eventType.includes('resolved'))
    return { icon: CheckCircle2, bg: 'bg-emerald-50', text: 'text-emerald-600', ring: 'ring-emerald-100/60', label: 'Resolution' }
  if (eventType.includes('priority') || eventType.includes('urgency') || eventType.includes('impact'))
    return { icon: Flag, bg: 'bg-rose-50', text: 'text-rose-600', ring: 'ring-rose-100/60', label: 'Priority' }
  if (eventType.includes('category') || eventType.includes('type') || eventType.includes('classification'))
    return { icon: Layers, bg: 'bg-amber-50', text: 'text-amber-600', ring: 'ring-amber-100/60', label: 'Classification' }
  if (eventType.includes('note'))
    return { icon: Lock, bg: 'bg-slate-50', text: 'text-slate-600', ring: 'ring-slate-100/60', label: 'Note' }
  if (eventType.includes('message'))
    return { icon: MessageSquare, bg: 'bg-cyan-50', text: 'text-cyan-600', ring: 'ring-cyan-100/60', label: 'Message' }
  if (eventType.includes('sla'))
    return { icon: Clock, bg: 'bg-orange-50', text: 'text-orange-600', ring: 'ring-orange-100/60', label: 'SLA' }
  if (eventType.includes('approval_requested'))
    return { icon: ShieldCheck, bg: 'bg-amber-50', text: 'text-amber-600', ring: 'ring-amber-100/60', label: 'Sent for Approval' }
  if (eventType.includes('approval_decided'))
    return { icon: CheckCircle2, bg: 'bg-emerald-50', text: 'text-emerald-600', ring: 'ring-emerald-100/60', label: 'Review Submitted' }
  return { icon: CircleDot, bg: 'bg-slate-50', text: 'text-slate-500', ring: 'ring-slate-100/60', label: 'Activity' }
}

function humanize(eventType: string): string {
  return eventType
    .replace(/[._]/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

function describe(entry: ActivityLogEntry): string {
  const desc = entry.details?.['description']
  if (typeof desc === 'string' && desc.trim()) return desc
  return humanize(entry.eventType)
}

function dateLabel(iso: string): string {
  // Day boundaries are evaluated in the selected timezone, so the "Today" /
  // "Yesterday" headings agree with the times stamped on the entries below.
  const now = new Date()
  const key = timezoneDayKey(iso)
  if (key === timezoneDayKey(now)) return 'Today'
  if (key === timezoneDayKey(new Date(now.getTime() - 86_400_000))) return 'Yesterday'

  return formatInTimezone(
    iso,
    { month: 'short', day: 'numeric', year: 'numeric' },
    'en-US',
  )
}

export default function ActivityTimeline({ entries }: { entries: ActivityLogEntry[] }): JSX.Element {
  if (entries.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-32">
        <div className="rounded-full bg-slate-50 p-6">
          <ActivityIcon className="h-12 w-12 text-slate-300" strokeWidth={1.5} />
        </div>
        <p className="mt-4 text-sm font-medium text-slate-500">No activity yet</p>
        <p className="mt-1 text-xs text-slate-400">Events will appear here</p>
      </div>
    )
  }

  let lastDate = ''

  return (
    <div className="space-y-0">
      {entries.map((e, idx) => {
        const actor = e.details?.['actorName']
        const cfg = iconConfigFor(e.eventType)
        const Icon = cfg.icon
        const dLabel = dateLabel(e.createdAt)
        const showDateHeader = dLabel !== lastDate
        lastDate = dLabel
        const isLast = idx === entries.length - 1

        return (
          <div key={e.id}>
            {showDateHeader && (
              <div className="flex items-center gap-3 pb-4 pt-6 first:pt-0">
                <div className="h-px flex-1 bg-slate-200" />
                <span className="px-3 py-1 text-[11px] font-semibold text-slate-500">
                  {dLabel}
                </span>
                <div className="h-px flex-1 bg-slate-200" />
              </div>
            )}
            <div className="flex gap-3.5">
              <div className="flex flex-col items-center">
                <div
                  className={cn(
                    'flex h-8 w-8 items-center justify-center rounded-full',
                    cfg.bg,
                    cfg.text,
                  )}
                >
                  <Icon className="h-4 w-4" strokeWidth={2} />
                </div>
                {!isLast && <div className="my-1 w-px flex-1 bg-slate-200" />}
              </div>
              <div className={cn('flex-1', isLast ? 'pb-0' : 'pb-5')}>
                <div className="rounded-[3px] border border-slate-200 bg-white p-3.5 transition-colors hover:border-slate-300">
                  <div className="flex items-start justify-between gap-3">
                    <p className="flex-1 text-sm leading-relaxed text-slate-800">{describe(e)}</p>
                    <span className={cn('shrink-0 rounded-[3px] px-2 py-0.5 text-[10px] font-semibold', cfg.bg, cfg.text)}>
                      {cfg.label}
                    </span>
                  </div>
                  {(e.eventType.includes('approval_requested') || e.eventType.includes('approval_decided')) && e.details && (
                    <div className="mt-2 space-y-0.5 text-xs text-slate-600">
                      {typeof e.details['levelName'] === 'string' && e.details['levelName'] && (
                        <div><span className="font-medium">Level:</span> {e.details['levelName']}</div>
                      )}
                      {typeof e.details['stateName'] === 'string' && e.details['stateName'] && (
                        <div><span className="font-medium">State:</span> {e.details['stateName']}</div>
                      )}
                      {typeof e.details['approverType'] === 'string' && e.details['approverType'] && (
                        <div><span className="font-medium">Approver Type:</span> {e.details['approverType'].charAt(0).toUpperCase() + e.details['approverType'].slice(1)}</div>
                      )}
                      {typeof e.details['approverName'] === 'string' && e.details['approverName'] && (
                        <div><span className="font-medium">Approver:</span> {e.details['approverName']}</div>
                      )}
                      {typeof e.details['senderRemark'] === 'string' && e.details['senderRemark'] && (
                        <div><span className="font-medium">Sender Remark:</span> {e.details['senderRemark']}</div>
                      )}
                      {typeof e.details['remark'] === 'string' && e.details['remark'] && (
                        <div><span className="font-medium">Review Remark:</span> {e.details['remark']}</div>
                      )}
                    </div>
                  )}
                  <div className="mt-3 flex items-center gap-2 text-xs text-slate-500">
                    {typeof actor === 'string' && actor && actor !== 'System' ? (
                      <>
                        <div className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-100 text-[10px] font-semibold text-slate-600">
                          {actor.charAt(0).toUpperCase()}
                        </div>
                        <span className="font-medium">{actor}</span>
                      </>
                    ) : (
                      <>
                        <div className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-100 text-[10px] font-semibold text-slate-400">
                          S
                        </div>
                        <span className="text-slate-400">System</span>
                      </>
                    )}
                    <span className="text-slate-300">•</span>
                    <span className="text-slate-400">{formatTime(e.createdAt)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
